import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi } from "service/authApi";
import { NOTIFY } from "constants/notifyType";
import Notify from "helpers/Notify";
import { RESPONSE_STATUSES } from "service/constants";
import { LOCAL } from "local/localizations";
import { usersApi } from "service/usersApi";
import { TWO_FA, REQUEST_STATUSES, ACCESS_TOKEN } from "constants/general";

export const login = createAsyncThunk(
  "/users/login",
  async ({ email, password, rem }, { dispatch }) => {
    const res = await authApi
      .login({
        email,
        password,
        rem,
      })
      .catch(({ response }) => {
        const backendMessage = response?.data?.message || response?.data?.error;

        if (backendMessage) {
          Notify(NOTIFY.type.error, backendMessage);
        } else if (response.status === RESPONSE_STATUSES.unauthorized) {
          Notify(NOTIFY.type.error, LOCAL.INVALID_LOGIN_OR_PASSWORD);
        } else if (response.status === RESPONSE_STATUSES.noPermission) {
          Notify(NOTIFY.type.error, LOCAL.USER_BLOCKED);
        } else if (response.status === RESPONSE_STATUSES.serverErr) {
          Notify(NOTIFY.type.error, LOCAL.SOMETHING_WANT_WRONG);
        } else {
          Notify(NOTIFY.type.error, LOCAL.SOMETHING_WANT_WRONG);
        }
        throw response;
      });

    //TODO open 2fa after deploy.
    // Store access token directly since 2FA is disabled
    const accessToken = res.data.accessToken;
    rem
      ? localStorage.setItem(ACCESS_TOKEN, accessToken)
      : sessionStorage.setItem(ACCESS_TOKEN, accessToken);

    return { ...res.data, email, rem };
  }
);

export const logout = createAsyncThunk("/users/logout", async () => {
  localStorage.removeItem("access_token");
  sessionStorage.removeItem("access_token");
  window.location.reload();
  return {};
});

export const getUser = createAsyncThunk(
  "getUser",
  async (_, { rejectWithValue }) => {
    try {
      const res = await usersApi.getUsers();
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

//TODO open 2fa after deploy.
// export const setup2FA = createAsyncThunk(
//   "/admin/2fa/setup",
//   async ({ email }) => {
//     const res = await authApi.setup2FA({ email }).catch(({ response }) => {
//       Notify(NOTIFY.type.error, LOCAL.SOMETHING_WANT_WRONG);
//       throw response;
//     });
//     return res.data;
//   }
// );

// export const verify2FA = createAsyncThunk(
//   "/admin/2fa/verify",
//   async ({ email, token }, { getState }) => {
//     const state = getState();
//     const { rem } = state.auth;
//     const res = await authApi
//       .verify2FA({ email, token })
//       .catch(({ response }) => {
//         Notify(NOTIFY.type.error, TWO_FA.invalid_code);
//         throw response;
//       });

//     // Store access token
//     const accessToken = res.data.accessToken;
//     rem
//       ? localStorage.setItem(ACCESS_TOKEN, accessToken)
//       : sessionStorage.setItem(ACCESS_TOKEN, accessToken);

//     return res.data;
//   }
// );

// export const verify2FALogin = createAsyncThunk(
//   "/admin/2fa/verify-login",
//   async ({ email, token }, { getState }) => {
//     const state = getState();
//     const { rem } = state.auth;
//     const res = await authApi
//       .verify2FALogin({ email, token })
//       .catch(({ response }) => {
//         Notify(NOTIFY.type.error, TWO_FA.invalid_code);
//         throw response;
//       });

//     // Store access token
//     const accessToken = res.data.accessToken;
//     rem
//       ? localStorage.setItem(ACCESS_TOKEN, accessToken)
//       : sessionStorage.setItem(ACCESS_TOKEN, accessToken);

//     return res.data;
//   }
// );

const initialState = {
  user: {
    email: "",
    password: "",
  },
  status: null,
  isAuth:
    !!localStorage.getItem("access_token") ||
    !!sessionStorage.getItem("access_token"),
  userData: {},
  userLoading: false,
  is2FAVerified: false,
  twoFA: {
    setupData: null,
    is2FAEnabled: false,
    requires2FA: false,
    verificationError: null,
  },
  rem: false,
};

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clear2FAError: (state) => {
      state.twoFA.verificationError = null;
    },
    setRequires2FA: (state, action) => {
      state.twoFA.requires2FA = action.payload;
    },
    clear2FASetup: (state) => {
      state.twoFA.setupData = null;
      state.twoFA.requires2FA = false;
    },
    setRem: (state, action) => {
      state.rem = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.twoFA.verificationError = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        //TODO open 2fa after deploy.
        // Directly authenticate without 2FA
        state.user = payload.user;
        state.status = "solved";
        state.isAuth = true;
        state.rem = payload.rem;

        // state.twoFA.requires2FA = true;
        // state.twoFA.is2FAEnabled = payload.is2FAEnabled;
        // state.status = REQUEST_STATUSES.twoFARequired;
        // state.rem = payload.rem;

        // if (payload.is2FAEnabled) {
        //   state.twoFA.setupData = null;
        // }
      })
      .addCase(login.rejected, (state) => {
        state.status = "rejected";
        state.twoFA.requires2FA = false;
      });
    builder
      .addCase(getUser.pending, (state) => {
        state.userLoading = true;
      })
      .addCase(getUser.fulfilled, (state, { payload }) => {
        state.userData = payload?.data;
        state.userLoading = false;
      })
      .addCase(getUser.rejected, (state) => {
        state.userLoading = false;
      });
    //TODO open 2fa after deploy.
    // .addCase(setup2FA.pending, (state) => {
    //   state.status = "loading";
    // })
    // .addCase(setup2FA.fulfilled, (state, { payload }) => {
    //   state.twoFA.setupData = payload;
    //   // Update is2FAEnabled status from setup response if provided
    //   if (payload.is2FAEnabled !== undefined) {
    //     state.twoFA.is2FAEnabled = payload.is2FAEnabled;
    //   }
    //   state.status = "2fa_required";
    // })
    // .addCase(setup2FA.rejected, (state) => {
    //   state.status = "rejected";
    // })
    // .addCase(verify2FA.pending, (state) => {
    //   state.status = "loading";
    //   state.twoFA.verificationError = null;
    // })
    // .addCase(verify2FA.fulfilled, (state, { payload }) => {
    //   state.user = payload.user;
    //   state.status = "solved";
    //   state.isAuth = true;
    //   state.is2FAVerified = true;
    //   state.twoFA.requires2FA = false;
    //   state.twoFA.is2FAEnabled = true;
    //   state.twoFA.setupData = null;
    // })
    // .addCase(verify2FA.rejected, (state) => {
    //   state.status = "rejected";
    //   state.twoFA.verificationError = TWO_FA.invalid_code;
    // })
    // .addCase(verify2FALogin.pending, (state) => {
    //   state.status = "loading";
    //   state.twoFA.verificationError = null;
    // })
    // .addCase(verify2FALogin.fulfilled, (state, { payload }) => {
    //   state.user = payload.user;
    //   state.status = "solved";
    //   state.isAuth = true;
    //   state.is2FAVerified = true;
    //   state.twoFA.requires2FA = false;
    // })
    // .addCase(verify2FALogin.rejected, (state) => {
    //   state.status = "rejected";
    //   state.twoFA.verificationError = TWO_FA.invalid_code;
    // });
  },
});
export const selectAdmin = (state) => state.user;

export const {
  adminLogin,
  clear2FAError,
  setRequires2FA,
  clear2FASetup,
  setRem,
} = adminSlice.actions;

export default adminSlice.reducer;
